import { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
    collection, onSnapshot, addDoc, doc, updateDoc, query, where, getDoc, getDocs, runTransaction
} from "firebase/firestore";
import toast from "react-hot-toast";

export function useLobby(user, balance) {
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [currentGameId, setCurrentGameId] = useState(null);
    const [pendingInvite, setPendingInvite] = useState(null); // {inviteId, to, timer}
    const INVITE_TIMEOUT = 30000; // 30 sec

    // Listen for *recent* online users
    useEffect(() => {
        // console.log('user', user);

        if (!user?.uid) return;
        const q = query(
            collection(db, "users"),
            where("online", "==", true)
        );
        const unsubUsers = onSnapshot(q, (snap) => {
            setOnlineUsers(
                snap.docs
                    .filter(doc => doc.id !== user.uid)
                    .filter(doc => {
                        const lastActive = doc.data().lastActive;
                        const balance = doc.data().balance || 0;
                        // Only users active in last 60s AND have enough balance
                        return lastActive && lastActive.toDate
                            ? (Date.now() - lastActive.toDate().getTime() < 60000) && balance >= 0.5
                            : false;
                    })
                    .map(doc => ({ userId: doc.id, ...doc.data() }))
            );
        });

        // Listen for invites *TO YOU* (pending only)
        const invitesQ = query(
            collection(db, "invites"),
            where("to", "==", user.uid),
            where("status", "==", "pending")
        );
        const unsubInvites = onSnapshot(invitesQ, (snap) => {
            setInvites(snap.docs.map(d => ({ inviteId: d.id, ...d.data() })));
        });

        // Listen for active games where you are a player (for auto-redirect)
        const gamesQ = query(
            collection(db, "games"),
            where("status", "==", "active"),
            where("players", "array-contains", user.uid)
        );
        const unsubGames = onSnapshot(gamesQ, (snap) => {
            // If a game is created and you're a player, setCurrentGameId
            if (!snap.empty) {
                const game = snap.docs[0];
                setCurrentGameId(game.id);
            }
        });

        return () => {
            unsubUsers();
            unsubInvites();
            unsubGames();
        };
    }, [user?.uid]);

    // Keep pendingInvite state synced with Firestore (no ghost invites after match)
    useEffect(() => {
        if (!user?.uid) return;

        const q = query(
            collection(db, "invites"),
            where("from.userId", "==", user.uid),
            where("status", "==", "pending")
        );
        const unsub = onSnapshot(q, (snap) => {
            if (snap.empty) {
                // No pending invites for this user: clear local state!
                setPendingInvite(null);
            } else {
                // There is still a pending invite, keep first one (no timer info here)
                const doc = snap.docs[0];
                setPendingInvite(prev => {
                    // keep local timer if exists (optional), or just set to Firestore data
                    return {
                        inviteId: doc.id,
                        to: doc.data().to,
                        timer: prev?.timer || null,
                    };
                });
            }
        });
        return unsub;
    }, [user?.uid]);


    const handleInvite = async (toUser, betAmount) => {
        if (pendingInvite) {
            toast.error("You already have a pending invite!");
            return;
        }
        if ((balance || 0) < 0.5) {
            toast.error("You need at least 0.5 USDT to play!");
            return;
        }
        if ((balance || 0) < betAmount) {
            toast.error("You need enough balance to play!");
            return;
        }
        if (currentGameId) {
            toast.error("Finish your current game first!");
            return;
        }

        if (toUser.isBot) {
            return {
                isBot: true,
                to: toUser,
                betAmount,
            };
        }
        // Check if already sent (just for double protection)
        const q = query(
            collection(db, "invites"),
            where("from.userId", "==", user.uid),
            where("status", "==", "pending")
        );
        const existing = await getDocs(q);
        if (!existing.empty) {
            toast.error("You already have a pending invite!");
            return;
        }

        // Send invite
        const docRef = await addDoc(collection(db, "invites"), {
            from: { userId: user.uid, username: user.displayName, avatar: user.photoURL },
            to: toUser.userId,
            createdAt: Date.now(),
            status: "pending",
            betAmount: Number(betAmount),
            gameType: "xo",
        });

        // Start timeout
        const timer = setTimeout(() => {
            handleDecline(docRef.id, true); // true means auto/expired
            setPendingInvite(null);
        }, INVITE_TIMEOUT);

        setPendingInvite({ inviteId: docRef.id, to: toUser.userId, timer });
    };


    const handleAccept = async (inviteId) => {
        const inviteRef = doc(db, "invites", inviteId);
        const inviteSnap = await getDoc(inviteRef);

        // if ((balance || 0) < 0.5) {
        //     toast.error("You need at least 0.5 USDT to play!");
        //     return;
        // }

        if (!inviteSnap.exists() || inviteSnap.data().status !== "pending") {
            alert("This invite is no longer available.");
            return;
        }
        const invite = inviteSnap.data();
        const bet = Number(invite.betAmount || 0.5);

        if ((balance || 0) < bet) {
            toast.error(`You need at least ${bet} USDT to play!`);
            return;
        }

        const p1Ref = doc(db, "users", invite.from.userId);
        const p2Ref = doc(db, "users", user.uid);

        try {
            await runTransaction(db, async (transaction) => {
                const p1Snap = await transaction.get(p1Ref);
                const p2Snap = await transaction.get(p2Ref);
                if (!p1Snap.exists() || !p2Snap.exists()) throw "User missing";
                const p1Balance = p1Snap.data().balance || 0;
                const p2Balance = p2Snap.data().balance || 0;
                if (p1Balance < bet || p2Balance < bet)
                    throw `Both players need at least ${bet} USDT`;
                transaction.update(p1Ref, { balance: p1Balance - bet });
                transaction.update(p2Ref, { balance: p2Balance - bet });
            });
        } catch (e) {
            toast.error(typeof e === "string" ? e : "Insufficient funds!");
            return;
        }

        const p1Id = invite.from.userId; // Player X
        const p2Id = user.uid;           // Player O

        const lastActiveObj = {
            [p1Id]: Date.now(),
            [p2Id]: Date.now()
        };

        // const gameRef = await addDoc(collection(db, "games"), {
        //     gameType: invite.gameType,
        //     playerX: invite.from,
        //     playerO: {
        //         userId: user.uid,
        //         username: user.displayName,
        //         avatar: user.photoURL
        //     },
        //     players: [invite.from.userId, user.uid],
        //     board: Array(9).fill(null),
        //     turn: "X",
        //     status: "active",
        //     winner: null,
        //     createdAt: Date.now(),
        //     updatedAt: Date.now(),
        //     lastActive: lastActiveObj,
        //     betAmount: bet,
        // });

        let gameData = {
            gameType: invite.gameType,
            players: [invite.from.userId, user.uid],
            betAmount: bet,
            status: "active",
            winner: null,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            lastActive: { [invite.from.userId]: Date.now(), [user.uid]: Date.now() },
        };

        if (invite.gameType === "xo") {
            gameData = {
                ...gameData,
                playerX: invite.from,
                playerO: {
                    userId: user.uid,
                    username: user.displayName,
                    avatar: user.photoURL,
                },
                board: Array(9).fill(null),
                turn: "X",
            };
        } else if (invite.gameType === "chess") {
            gameData = {
                ...gameData,
                player1: invite.from,
                player2: {
                    userId: user.uid,
                    username: user.displayName,
                    avatar: user.photoURL,
                },
                moves: [],
                turn: "w",
            };
        } else if (invite.gameType === "rps") {
            gameData = {
                ...gameData,
                player1: invite.from,
                player2: {
                    userId: user.uid,
                    username: user.displayName,
                    avatar: user.photoURL,
                },
                moves: {},
            };
        }

        const gameRef = await addDoc(collection(db, "games"), gameData);


        setCurrentGameId(gameRef.id);
        setPendingInvite(null);
        await updateDoc(doc(db, "invites", inviteId), { status: "accepted" });
    };


    const handleDecline = async (inviteId, auto = false) => {
        if (!inviteId) {
            toast.error("Invalid invitation. (No ID)");
            return;
        }
        try {
            const ref = doc(db, "invites", inviteId);
            const snap = await getDoc(ref);

            if (!snap.exists()) {
                toast.error("Invite does not exist!");
                return;
            }
            if (snap.data().status !== "pending") {
                toast.error(`Invite is not pending. Status: ${snap.data().status}`);
                return;
            }
            await updateDoc(ref, { status: auto ? "expired" : "declined" });
            if (auto) {
                toast.error("Invite expired (not accepted in time).");
            } else {
                toast.success("Declined successfully!");
            }
            setPendingInvite(null);
        } catch (err) {
            toast.error("Decline error: " + (err?.message || err));
            console.error("Decline error:", err);
        }
    };


    return {
        onlineUsers,
        invites,
        currentGameId,
        setCurrentGameId,
        handleInvite,
        handleAccept,
        handleDecline
    };
}
